import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, List, ListItemButton,
  ListItemText, IconButton, Chip, Button, InputAdornment,
  Paper, Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import {
  getAllPatterns,
  loadPattern,
  deletePattern,
  toggleFavorite,
  searchPatterns
} from '../utils/patternManager';
import { track } from '../utils/analytics';

const PatternsPanel = ({ 
  onLoadPattern, 
  onSaveCurrentPattern,
  onBack,
  currentRootNote,
  currentMode,
  inDrawer = false
}) => {
  const [patterns, setPatterns] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPatterns, setFilteredPatterns] = useState([]);
  
  const refreshPatterns = () => {
    const loadedPatterns = getAllPatterns();
    setPatterns(loadedPatterns);
    setFilteredPatterns(searchPatterns(loadedPatterns, searchTerm));
  };
  
  // Load patterns on component mount
  useEffect(() => {
    refreshPatterns();
  }, []);
  
  // Refresh patterns when one is saved from SavePatternDialog (patternSaved event)
  useEffect(() => {
    const handleStorageChange = () => {
      refreshPatterns();
    };
    
    window.addEventListener('patternSaved', handleStorageChange);
    return () => {
      window.removeEventListener('patternSaved', handleStorageChange);
    };
  }, []);
  
  // Filter patterns when search term changes
  useEffect(() => {
    setFilteredPatterns(searchPatterns(patterns, searchTerm));
  }, [searchTerm, patterns]);
  
  // Handle pattern operations
  const handleDeletePattern = (id, e) => {
    e.stopPropagation();
    deletePattern(id);
    track('pattern_deleted');
    const updatedPatterns = getAllPatterns();
    setPatterns(updatedPatterns);
  };
  
  const handleToggleFavorite = (id, e) => {
    e.stopPropagation();
    toggleFavorite(id);
    const updatedPatterns = getAllPatterns();
    setPatterns(updatedPatterns);
  };
  
  const handleLoadPattern = (pattern) => {
    if (onLoadPattern) {
      onLoadPattern(pattern);
    }
  };
  
  // Format date
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  return (
    <Box className="patterns-panel" sx={{ width: '100%', mt: inDrawer ? 1 : 3, p: inDrawer ? 1 : 0 }}>
      {!inDrawer && (
        <Typography variant="h5" component="h1" gutterBottom className="title1" sx={{ mb: 3 }}>
          Pattern Library
        </Typography>
      )}
      
      {/* Search bar */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'rgba(30, 30, 47, 0.5)' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search patterns..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        />
      </Paper>
      
      {/* Pattern list */}
      {filteredPatterns.length > 0 ? (
        <List sx={{ width: '100%', mb: 3 }}>
          {filteredPatterns.map((pattern) => (
            <Paper
              key={pattern.id}
              elevation={0}
              sx={{
                mb: 2,
                bgcolor: 'rgba(30, 30, 47, 0.5)',
                borderRadius: '8px',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'rgba(30, 30, 47, 0.7)',
                  transform: 'translateY(-2px)',
                }
              }}
            >
              <ListItemButton
                onClick={() => handleLoadPattern(pattern)}
                sx={{
                  py: 1.5,
                  px: 2,
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  flexGrow: 1
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%'
                  }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontSize: '1.1rem',
                        fontWeight: 'medium'
                      }}
                    >
                      {pattern.title}
                    </Typography>
                    <Box>
                      <IconButton
                        edge="end"
                        aria-label="favorite"
                        onClick={(e) => handleToggleFavorite(pattern.id, e)}
                        sx={{ color: pattern.favorite ? 'gold' : 'rgba(255, 255, 255, 0.5)' }}
                        size="small"
                      >
                        {pattern.favorite ? <StarIcon /> : <StarBorderIcon />}
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={(e) => handleDeletePattern(pattern.id, e)}
                        sx={{ color: 'error.light' }}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Chip 
                    label={`${pattern.rootNote} ${pattern.currentMode}`}
                    size="small"
                    sx={{ 
                      bgcolor: 'secondary.dark', 
                      color: 'white',
                      fontSize: '0.7rem'
                    }}
                  />
                  {pattern.currentProgression && (
                    <Chip
                      label={pattern.currentProgression}
                      size="small"
                      sx={{ 
                        ml: 1,
                        bgcolor: 'primary.dark',
                        color: 'white',
                        fontSize: '0.7rem'
                      }}
                    />
                  )}
                  <Typography 
                    variant="caption" 
                    sx={{ ml: 1, color: 'text.secondary' }}
                  >
                    {formatDate(pattern.timestamp)}
                  </Typography>
                </Box>
              </Box>
              </ListItemButton>
            </Paper>
          ))}
        </List>
      ) : (
        <Paper
          elevation={0}
          sx={{
            py: 4,
            px: 2,
            mb: 3,
            bgcolor: 'rgba(30, 30, 47, 0.5)',
            borderRadius: '8px',
            textAlign: 'center'
          }}
        >
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {searchTerm 
              ? 'No patterns match your search. Try different keywords.'
              : 'No patterns found. Save your current pattern to see it here.'}
          </Typography>
        </Paper>
      )}
      
      {/* Current scale info */}
      {currentRootNote && currentMode && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            bgcolor: 'rgba(30, 30, 47, 0.5)',
            borderRadius: '8px'
          }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            Current scale:
          </Typography>
          <Chip 
            label={`${currentRootNote} ${currentMode}`}
            color="secondary"
            sx={{ fontWeight: 'medium' }}
          />
        </Paper>
      )}
      
      {/* Action buttons */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: inDrawer ? 'center' : 'space-between' }}>
        {!inDrawer && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        )}
        <Button
          variant="contained"
          color="secondary"
          onClick={onSaveCurrentPattern}
          startIcon={<SaveIcon />}
          fullWidth={inDrawer}
        >
          Save Current Pattern
        </Button>
      </Box>
    </Box>
  );
};

export default PatternsPanel;
